// Harmony of a Mercadini pairing: pair groups and score. Plain module (no JSX, no imports) so it can be unit tested with node:
// `node src/pairingHarmony.test.mjs`.

const FOOD_KEYS = ['food_sweet_tendency', 'food_fatness', 'food_succulence', 'food_unctuousness', 'food_sapidity', 'food_bitter_tendency',
  'food_acid_tendency', 'food_sweetness', 'food_spiciness', 'food_aromaticity', 'food_persistence', 'food_structure'];
const WINE_KEYS = ['wine_alcohol', 'wine_tannicity', 'wine_acidity', 'wine_effervescence', 'wine_sapidity', 'wine_sweetness', 'wine_softness',
  'wine_olfactory_intensity', 'wine_body', 'wine_pai'];
const isFilledVal = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

// Food/wine pairs grouped by function. Contrast pairs want similar intensities (low delta = balance);
// concordance pairs want the values to coincide.
export const PAIR_GROUPS = [
  {
    name: 'pairing.group.1',
    kind: 'contrast',
    pairs: [
      { food: 'food_succulence', wine: 'wine_alcohol' },
      { food: 'food_unctuousness', wine: 'wine_tannicity' },
    ],
  },
  {
    name: 'pairing.group.2',
    kind: 'contrast',
    pairs: [
      { food: 'food_sweet_tendency', wine: 'wine_acidity' },
      { food: 'food_fatness', wine: 'wine_effervescence' },
      { food: 'food_fatness', wine: 'wine_sapidity' },
    ],
  },
  {
    name: 'pairing.group.3',
    kind: 'contrast',
    pairs: [
      { food: 'food_sapidity', wine: 'wine_softness' },
      { food: 'food_bitter_tendency', wine: 'wine_alcohol' },
      { food: 'food_acid_tendency', wine: 'wine_softness' },
    ],
  },
  {
    name: 'pairing.group.4',
    kind: 'concordance',
    pairs: [
      { food: 'food_sweetness', wine: 'wine_sweetness' },
    ],
  },
  {
    name: 'pairing.group.5',
    kind: 'concordance',
    pairs: [
      { food: 'food_structure', wine: 'wine_body' },
    ],
  },
  {
    name: 'pairing.group.6',
    kind: 'concordance',
    pairs: [
      { food: 'food_aromaticity', wine: 'wine_olfactory_intensity' },
      { food: 'food_spiciness', wine: 'wine_olfactory_intensity' },
    ],
  },
  {
    name: 'pairing.group.7',
    kind: 'concordance',
    pairs: [
      { food: 'food_persistence', wine: 'wine_pai' },
    ],
  },
];

// Flat list used to compute the harmony (each pair keeps the kind of its group)
const ALL_PAIRS = PAIR_GROUPS.flatMap((group) => group.pairs.map((pair) => ({ ...pair, kind: group.kind })));

// The didactic definitions (the "?" button) live in the translations: pairing.def.<field>

// The Mercadini method needs at least this many filled-in sensations on each side to judge a pairing
export const MIN_FILLED_PER_SIDE = 3;

// ── Harmony score (the app's own simplification: the AIS method gives NO numeric formula, it judges the similarity of the
// two polygons: "every wine characteristic must oppose or agree with a food characteristic with a comparable intensity") ──
// * the pairing is judged ONLY when all 22 Mercadini values (12 dish + 10 wine) are filled in: with anything missing there is no score and
//   no outcome, and the missing values are listed so the user has to enter them (not filled in is never 0);
// * contrast pairs are skipped when the dish sensation is slight (< RELEVANCE_MIN): it asks for no counterpart;
//   the sweetness concordance is skipped when neither side has sweetness, the spiciness one when the dish has no spices;
// * the difference between the two values is the penalty; concordance pairs weigh CONCORDANCE_WEIGHT times more;
// * if no pair is relevant, every complete pair is used;
// * score = 10 - weighted mean difference; when an essential concordance (sweetness, structure, aromaticity, persistence) differs by
//   4 or more the score cannot exceed 5.9, if it differs by 5 or more it cannot exceed 4.9 (disharmonious): a clear mismatch must not be
//   hidden by the other pairs;
// * outcome from the score: >= 7 harmonious, >= 6 fairly, >= 5 slightly, below disharmonious.
export const RELEVANCE_MIN = 3;
export const CONCORDANCE_WEIGHT = 2;
export const ESSENTIAL_FOOD = ['food_sweetness', 'food_structure', 'food_aromaticity', 'food_persistence'];
export const ESSENTIAL_GAP = 4;
export const ESSENTIAL_CAP = 5.9;
export const ESSENTIAL_SEVERE_GAP = 5;
export const ESSENTIAL_SEVERE_CAP = 4.9;
export const OUTCOME_BANDS = [[7, 'harmonious'], [6, 'fairly_harmonious'], [5, 'slightly_harmonious']];
export const outcomeFromScore = (score) => { const band = OUTCOME_BANDS.find(([min]) => score >= min); return band ? band[1] : 'disharmonious'; };
export { FOOD_KEYS, WINE_KEYS };

// `options.severe = false` reproduces the previous version of the rules (single cap at 5.9), used only to recognise old stored results
export function evaluateHarmony(form, options = {}) {
  const missingFood = FOOD_KEYS.filter((key) => !isFilledVal(form[key]));
  const missingWine = WINE_KEYS.filter((key) => !isFilledVal(form[key]));
  const foodFilled = FOOD_KEYS.length - missingFood.length;
  const wineFilled = WINE_KEYS.length - missingWine.length;
  if (missingFood.length > 0 || missingWine.length > 0) {
    return { enough: false, code: '', score: 0, pairs: 0, foodFilled, wineFilled, missingFood, missingWine };
  }
  const complete = ALL_PAIRS.filter((pair) => isFilledVal(form[pair.food]) && isFilledVal(form[pair.wine]));
  const relevant = complete.filter((pair) => {
    const food = Number(form[pair.food]);
    const wine = Number(form[pair.wine]);
    if (pair.kind === 'contrast') return food >= RELEVANCE_MIN;
    if (pair.food === 'food_sweetness') return food >= RELEVANCE_MIN || wine >= RELEVANCE_MIN;   // a sweet wine is a mismatch with a dish that is not sweet
    if (pair.food === 'food_spiciness') return food >= RELEVANCE_MIN;                              // spices ask for an intense wine; no spice, no counterpart
    return true;
  });
  const used = relevant.length > 0 ? relevant : complete;
  const base = { foodFilled, wineFilled, pairs: used.length, missingFood, missingWine };
  let penalty = 0;
  let weight = 0;
  let capped = false;
  let maxEssentialGap = 0;
  for (const pair of used) {
    const diff = Math.abs(Number(form[pair.food]) - Number(form[pair.wine]));
    const w = pair.kind === 'concordance' ? CONCORDANCE_WEIGHT : 1;
    penalty += w * diff;
    weight += w;
    if (pair.kind === 'concordance' && ESSENTIAL_FOOD.includes(pair.food)) maxEssentialGap = Math.max(maxEssentialGap, diff);
  }
  const avgDiff = penalty / weight;
  let score = Math.max(0, 10 - avgDiff);
  if (options.severe !== false && maxEssentialGap >= ESSENTIAL_SEVERE_GAP) { score = Math.min(score, ESSENTIAL_SEVERE_CAP); capped = true; }
  else if (maxEssentialGap >= ESSENTIAL_GAP) { score = Math.min(score, ESSENTIAL_CAP); capped = true; }
  return { ...base, enough: true, code: outcomeFromScore(score), score, avgDiff, capped };
}


// ── Previous calculation (until 2026-10-04): mean absolute difference over every complete pair, score = 10 - mean, thresholds on
// the mean (<= 1.5, 3, 5). Kept ONLY to recognise scores/outcomes that were stored automatically with it: when an old pairing is
// reopened for editing, those stored values are dropped so that saving recalculates them with the current rules, while a score
// typed by hand (different from this calculation) is kept.
export function legacyEvaluateHarmony(form) {
  const foodFilled = FOOD_KEYS.filter((key) => isFilledVal(form[key])).length;
  const wineFilled = WINE_KEYS.filter((key) => isFilledVal(form[key])).length;
  const complete = ALL_PAIRS.filter((pair) => isFilledVal(form[pair.food]) && isFilledVal(form[pair.wine]));
  if (foodFilled < MIN_FILLED_PER_SIDE || wineFilled < MIN_FILLED_PER_SIDE || complete.length === 0) return { enough: false, code: '', score: 0 };
  const avg = complete.reduce((sum, pair) => sum + Math.abs(Number(form[pair.food]) - Number(form[pair.wine])), 0) / complete.length;
  if (avg <= 1.5) return { enough: true, code: 'harmonious', score: 10 - avg };
  if (avg <= 3) return { enough: true, code: 'fairly_harmonious', score: 10 - avg };
  if (avg <= 5) return { enough: true, code: 'slightly_harmonious', score: 10 - avg };
  return { enough: true, code: 'disharmonious', score: Math.max(0, 10 - avg) };
}

// Previous versions of the rules, newest first: a score/outcome stored automatically by any of them is recognised as such.
const PREVIOUS_FORMULAS = [
  (form) => evaluateHarmony(form, { severe: false }),   // until the severe cap (gap >= 5 -> 4.9) was added
  legacyEvaluateHarmony,                                // until the relevance filter / weights / caps were added
];

// Drops from a loaded pairing the score/outcome that were stored automatically by a previous version of the rules, so that saving
// recalculates them with the current one. A score typed by hand (different from every automatic result) is kept.
export function withoutStaleAutomaticResult(loaded) {
  const out = { ...loaded };
  const current = evaluateHarmony(loaded);          // not enough (values missing) -> score 0, code '': any stored automatic result is stale
  for (const formula of PREVIOUS_FORMULAS) {
    const old = formula(loaded);
    if (!old.enough) continue;
    if (isFilledVal(out.score) && Math.abs(Number(out.score) - Math.round(old.score * 10) / 10) < 0.05
        && Math.abs(Number(out.score) - Math.round(current.score * 10) / 10) >= 0.05) out.score = '';
    if (out.pairing_outcome === old.code && out.pairing_outcome !== current.code) out.pairing_outcome = '';
  }
  return out;
}
